/* -------------------------------------------------------------------------- */
/*                       ANCHOR Formatos (es-MX)                               */
/* -------------------------------------------------------------------------- */

const NUM = new Intl.NumberFormat('es-MX');

export function fmtNumber(n: number | null | undefined): string {
  return NUM.format(n ?? 0);
}

export function initials(text: string | null | undefined, max = 2): string {
  const words = (text || '').replace(/[^\p{L}\p{N}\s&]/gu, ' ').split(/\s+/).filter(w => w && w !== '&');
  if (!words.length) return '·';
  if (words.length === 1) return words[0].slice(0, 2).replace(/^./, c => c.toUpperCase());
  return words.slice(0, max).map(w => w[0].toUpperCase()).join('');
}

export function personName(u: { names?: string; paternalLastName?: string | null } | null | undefined): string {
  if (!u) return '';
  return [u.names, u.paternalLastName].filter(Boolean).join(' ');
}

export function shortPersonName(u: { names?: string; paternalLastName?: string | null } | null | undefined): string {
  if (!u) return '';
  const first = (u.names || '').split(' ')[0];
  return u.paternalLastName ? `${first} ${u.paternalLastName[0]}.` : first;
}

export function parseDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  // El backend envía fechas sin zona (UTC); se interpretan como UTC.
  const iso = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value) ? value : value + 'Z';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

export function shortDate(value: string | Date | null | undefined, withYear = false): string {
  const d = parseDate(value);
  if (!d) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${withYear ? ' ' + d.getFullYear() : ''}`;
}

export function hourMinute(value: string | Date | null | undefined): string {
  const d = parseDate(value);
  if (!d) return '';
  return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** «hace 4 min», «hace 2 h», «ayer», «12 sep». */
export function relativeTime(value: string | Date | null | undefined, now = new Date()): string {
  const d = parseDate(value);
  if (!d) return '';
  const diff = Math.max(0, (now.getTime() - d.getTime()) / 1000);
  if (diff < 60) return 'hace un momento';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400 && d.getDate() === now.getDate()) return `hace ${Math.floor(diff / 3600)} h`;
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'ayer';
  if (d.toDateString() === now.toDateString()) return `hace ${Math.floor(diff / 3600)} h`;
  return shortDate(d);
}

/** Etiqueta de día para agrupar actividad: HOY, AYER o fecha. */
export function dayLabel(value: string | Date | null | undefined, now = new Date()): string {
  const d = parseDate(value);
  if (!d) return '';
  if (d.toDateString() === now.toDateString()) return 'Hoy';
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Ayer';
  return shortDate(d, d.getFullYear() !== now.getFullYear());
}

export function fileSize(bytes: number | null | undefined): string {
  const b = bytes ?? 0;
  if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(b / 1024))} KB`;
}

export function fileExt(mime: string | null | undefined): string {
  if (!mime) return '';
  if (mime.includes('svg')) return 'SVG';
  if (mime.includes('jpeg')) return 'JPG';
  return (mime.split('/')[1] || '').toUpperCase();
}

/** Palabra a escribir para confirmar la eliminación del negocio (contrato: nombre en MAYÚSCULAS). */
export function confirmWord(name: string): string {
  return (name || '').toUpperCase();
}

export function plural(n: number, one: string, many: string): string {
  return `${fmtNumber(n)} ${n === 1 ? one : many}`;
}
